import React, { useState, useEffect, useRef } from 'react';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import './internos.css';
import VisibleAccess from '../../permissions/VisibleAccess';
import useSubmitLock from '../../utils/useSubmitLock';
import Lupa from '../../assets/icons/lupa.svg';
import '../../components/SearchInternal/SearchInternal.css';
import Modal from '../../components/modal';
import { locationIdFromTypedText, locationLabel, validateLocationSelection } from '../../utils/locationSelection';

// Função para formatar a data no formato apropriado para o input de data
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const safeText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    if (v.name && typeof v.name !== 'object') return String(v.name);
    if (v.label && typeof v.label !== 'object') return String(v.label);
    try { return JSON.stringify(v); } catch { return ''; }
  }
  return String(v);
};

function EditInternal({ internalDetails, onCloseModal, onSaveSuccess, unitId }) {
  const { updateInternal, listInternalRegime, listInternalConduct, listFactionrelationship, listInternals } = useInternals();
  const { listColorEthnicity, listGender, listMaritalStatus, listSexualOrientation, listCountry, listCity } = useAuxiliaries();

  const [akaList, setAkaList] = useState(internalDetails.aka || []);
  const [colorsEthnicity, setColorsEthnicity] = useState([]);
  const [genders, setGenders] = useState([]);
  const [sexualOrientations, setSexualOrientations] = useState([]);
  const [maritalStatuses, setMaritalStatuses] = useState([]);
  // Estados para busca dinâmica de países e cidades
  const [country, setCountry] = useState([]);
  // O GET devolve o texto de país/cidade; o ID só existe quando o usuário escolhe na lista
  const [nationalityName, setNationalityName] = useState(locationLabel(internalDetails.nationality_country));
  const [naturalnessName, setNaturalnessName] = useState(locationLabel(internalDetails.naturalness_city));
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [shouldSearchCity, setShouldSearchCity] = useState(false);
  const originalDataRef = useRef(null);

  // Regime, conduta e facção
  const [conducts, setConducts] = useState([]);
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const [selectedLink, setSelectedLink] = useState(null);
  const [isLinkSaving, setIsLinkSaving] = useState(false);
  const [currentLink, setCurrentLink] = useState(
    internalDetails.other ? { id: internalDetails.other, name: internalDetails.other_name || internalDetails.other } : null
  );
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [isLinkSearching, setIsLinkSearching] = useState(false);
  const [showLinkConfirm, setShowLinkConfirm] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  const [formData, setFormData] = useState({
    name: internalDetails.name || '',
    conduct: internalDetails.conduct?.id || '',
    regime: internalDetails.regime?.id || '',
    cpf: internalDetails.cpf || '',
    rg: internalDetails.rg || '',
    date_of_birth: formatDate(internalDetails.date_of_birth) || '',
    mothers_name: internalDetails.mothers_name || '',
    nationality_country: '',
    naturalness_city: '',
    fathers_name: internalDetails.fathers_name || '',
    color_or_ethnicity: '',
    gender: '', 
    sexual_orientation: internalDetails.sexual_orientation || '',
    marital_status: '', 
    social_name: internalDetails.social_name || '',
    has_biometrics: internalDetails.has_biometrics ?? internalDetails.biometrics ?? false,
    rji_biometrics: internalDetails.rji_biometrics || '',
    tguid_biometrics: internalDetails.tguid_biometrics || '',
    biometrics_observation: internalDetails.biometrics_observation || internalDetails.biometrics_observations || '',
  });

  // Estado inicial do formulário: base de comparação enquanto originalDataRef ainda não foi populado
  const initialFormDataRef = useRef(formData);

  useEffect(() => {
    const fetchAuxiliaryData = async () => {
      const colors = await listColorEthnicity();
      const genders = await listGender();
      const sexualOrientations = await listSexualOrientation();
      const maritalStatuses = await listMaritalStatus();
      // Busca países e cidades
      let countries = [];
      try {
        const countriesRes = await listCountry();
        countries = countriesRes && countriesRes.results ? countriesRes.results : [];
      } catch (e) {
        // ignora erro de países/cidades
      }
      setCountry(countries);

      setColorsEthnicity(colors?.results || colors || []);
      setGenders(genders?.results || genders || []);
      setSexualOrientations(sexualOrientations?.results || sexualOrientations || []);
      setMaritalStatuses(maritalStatuses?.results || maritalStatuses || []);

      // Busca, condutas e facções
      const conductsRes = await listInternalConduct();
      setConducts(conductsRes?.results || []);

      const updates = {
        color_or_ethnicity: colors.find(c => c.name === internalDetails.color_or_ethnicity)?.id || '',
        gender: genders.find(g => g.name === internalDetails.gender)?.id || '',
        marital_status: maritalStatuses.find(m => m.name === internalDetails.marital_status)?.id || '',
        sexual_orientation: internalDetails.sexual_orientation || '',
        regime: internalDetails.regime?.id || '',
        conduct: internalDetails.conduct?.id || '',
      };

      setFormData(prev => ({ ...prev, ...updates }));

      // Captura o estado inicial mapeado para comparação posterior
      originalDataRef.current = {
        name: internalDetails.name || '',
        conduct: internalDetails.conduct?.id || '',
        regime: internalDetails.regime?.id || '',
        cpf: internalDetails.cpf || '',
        rg: internalDetails.rg || '',
        date_of_birth: formatDate(internalDetails.date_of_birth) || '',
        mothers_name: internalDetails.mothers_name || '',
        nationality_country: '',
        naturalness_city: '',
        fathers_name: internalDetails.fathers_name || '',
        color_or_ethnicity: updates.color_or_ethnicity,
        gender: updates.gender,
        sexual_orientation: updates.sexual_orientation,
        marital_status: updates.marital_status,
        social_name: internalDetails.social_name || '',
        has_biometrics: internalDetails.has_biometrics ?? internalDetails.biometrics ?? false,
        rji_biometrics: internalDetails.rji_biometrics || '',
        tguid_biometrics: internalDetails.tguid_biometrics || '',
        biometrics_observation: internalDetails.biometrics_observation || internalDetails.biometrics_observations || '',
        aka: [...(internalDetails.aka || [])]
      };
    };

    fetchAuxiliaryData();
  }, [internalDetails, listColorEthnicity, listGender, listMaritalStatus, listSexualOrientation, listCountry, listCity, listInternalRegime, listInternalConduct, listFactionrelationship]);

  // Função para filtrar sugestões de países
  const handleCountrySearch = (query) => {
    setNationalityName(query);
    setFormData((prev) => ({ ...prev, nationality_country: locationIdFromTypedText(query) }));
    if (query.trim() === '') {
      setFilteredCountries([]);
    } else {
      const filtered = country.filter((c) =>
        c.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  };

  // Função para filtrar sugestões de cidades
  const handleCitySearch = (query) => {
    setNaturalnessName(query);
    setFormData((prev) => ({ ...prev, naturalness_city: locationIdFromTypedText(query) }));
    setShouldSearchCity(true);
    setCitySearchQuery(query);
  };

  const selectCountry = (selectedCountry) => {
    setNationalityName(selectedCountry.name);
    setFormData((prev) => ({ ...prev, nationality_country: selectedCountry.id }));
    setFilteredCountries([]);
  };

  const selectCity = (selectedCity) => {
    setNaturalnessName(selectedCity.name);
    setFormData((prev) => ({ ...prev, naturalness_city: selectedCity.id }));
    setFilteredCities([]);
    setShouldSearchCity(false);
    setCitySearchQuery(selectedCity.name);
  };

  useEffect(() => {
    const query = String(citySearchQuery || '').trim();

    if (!shouldSearchCity || query.length < 3) {
      setFilteredCities([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const cityResponse = await listCity({ name: query });
        setFilteredCities(cityResponse?.results || []);
      } catch (error) {
        console.error('Erro ao buscar cidades no editar interno:', error);
        setFilteredCities([]);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [citySearchQuery, listCity, shouldSearchCity]);


  useEffect(() => {
    const query = linkSearch.trim();
    if (query.length < 3) {
      setLinkResults([]);
      setIsLinkSearching(false);
      return;
    }
    const isOnlyDigits = /^\d+$/.test(query);
    const searchParam = isOnlyDigits ? { cpf: query } : { name: query };
    setIsLinkSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await listInternals(searchParam);
        const results = Array.isArray(response?.results) ? response.results : [];
        setLinkResults(results.filter((r) => String(r.id) !== String(internalDetails.id)));
      } catch {
        setLinkResults([]);
      } finally {
        setIsLinkSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [linkSearch, listInternals, internalDetails.id]);

  const handleLinkSelect = (internal) => {
    setSelectedLink(internal);
    setLinkSearch('');
    setLinkResults([]);
  };

  const handleSaveLink = async () => {
    if (!selectedLink) return;
    setIsLinkSaving(true);
    try {
      await updateInternal(internalDetails.id, { other: selectedLink.id, main: false });
      setCurrentLink({ id: selectedLink.id, name: selectedLink.name });
      setSelectedLink(null);
      setShowLinkConfirm(false);
      onSaveSuccess();
    } finally {
      setIsLinkSaving(false);
    }
  };

  const handleUnlink = async () => {
    setIsLinkSaving(true);
    try {
      await updateInternal(internalDetails.id, { other: null, main: true });
      setCurrentLink(null);
      setSelectedLink(null);
      setShowUnlinkConfirm(false);
      onSaveSuccess();
    } finally {
      setIsLinkSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const selectFields = [
      'color_or_ethnicity',
      'gender',
      'marital_status',
      'regime',
      'conduct',
      'faction_relationship',
      'sexual_orientation'
    ];
    const parsedValue = selectFields.includes(name)
      ? (value === '' ? '' : Number(value))
      : (typeof value === 'object' && value !== null ? value.id : value);
    setFormData(prevState => ({
      ...prevState,
      [name]: parsedValue
    }));
  };

  const handleAkaChange = (index, value) => {
    const updatedAkaList = [...akaList];
    updatedAkaList[index] = value;
    setAkaList(updatedAkaList);
  };

  const handleAddAka = () => {
    setAkaList([...akaList, '']);
  };

  const handleRemoveAka = (index) => {
    const updatedAkaList = akaList.filter((_, i) => i !== index);
    setAkaList(updatedAkaList);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      if (!validateLocationSelection(formData)) return;

      const currentAka = akaList
        .filter(aka => aka.trim() !== '')
        .map(aka => aka.toUpperCase());

      // Se as listas auxiliares ainda não carregaram, compara com o estado inicial (nunca envia tudo)
      const original = originalDataRef.current
        || { ...initialFormDataRef.current, aka: [...(internalDetails.aka || [])] };
      const changedPayload = {};

      // Comparação de arrays (aka)
      const sortedCurrentAka = [...currentAka].sort();
      const sortedOriginalAka = (original.aka || []).map(aka => String(aka).toUpperCase()).sort();
      if (JSON.stringify(sortedCurrentAka) !== JSON.stringify(sortedOriginalAka)) {
        changedPayload.aka = currentAka;
      }

      // Comparação de campos simples
      Object.keys(formData).forEach(key => {
        const newVal = formData[key];
        const oldVal = original[key];
        // Compara como string para evitar inconsistências de tipos (ex: "1" vs 1)
        if (String(newVal ?? '') !== String(oldVal ?? '')) {
          changedPayload[key] = newVal;
        }
      });

      // País/cidade esvaziados pelo usuário (tinham valor no GET): envia null para limpar
      if (locationLabel(internalDetails.nationality_country) && !nationalityName.trim()) {
        changedPayload.nationality_country = null;
      }
      if (locationLabel(internalDetails.naturalness_city) && !naturalnessName.trim()) {
        changedPayload.naturalness_city = null;
      }

      if (Object.keys(changedPayload).length === 0) {
        onCloseModal();
        return;
      }

      // Reaproveitamento da lógica de limpeza original no payload filtrado
      if (changedPayload.hasOwnProperty('conduct') && !changedPayload.conduct) {
        delete changedPayload.conduct;
      }

      if (changedPayload.hasOwnProperty('biometrics')) {
        delete changedPayload.biometrics;
      }

      await updateInternal(internalDetails.id, changedPayload);
      onCloseModal();
      onSaveSuccess();
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        {/* disciplina e administrativo veem */}
        <VisibleAccess compareField="groups" compareValues={["DISCIPLINA", "ADMINISTRATIVO"]}>
            <div className="form-group">
              <label>Conduta</label>
              <select
                className="custom-select-conduct"
                name="conduct"
                value={formData.conduct}
                onChange={handleChange}
              >
                <option value="">Selecione</option>
                {conducts.map(c => (
                  <option key={c.id} value={c.id}>{safeText(c.name)}</option>
                ))}
              </select>
            </div>
        </VisibleAccess>

        <div className='div-form-container'>
          <div className='form-group modal-form'>
            <label>Nome:</label>
            <FormattedInput
              type="nome"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          <div className='form-group modal-form'>
            <label>Nome social:</label>
            <FormattedInput
              type="nome"
              name="social_name"
              value={formData.social_name}
              onChange={handleChange}
            />
          </div>

          <div className='form-group modal-form'>
            <label>Vulgos:</label>
            {akaList.map((aka, index) => (
              <div key={index} className='aka-input'>
                <input
                  type="text"
                  value={aka}
                  onChange={(e) => handleAkaChange(index, e.target.value)}
                  className='aka-field'
                />
                <button type="button" onClick={() => handleRemoveAka(index)} className='aka-remove-btn'>×</button>
              </div>
            ))}
            <button type="button" onClick={handleAddAka} className='add-aka-btn'>Adicionar outro</button>
          </div>

          <div className='form-group modal-form'>
            <label>Nome da Mãe</label>
            <FormattedInput
              type="nome"
              name="mothers_name"
              value={formData.mothers_name}
              onChange={handleChange}
            />
          </div>
          <div className='form-group modal-form'>
            <label>Nome do Pai</label>
            <FormattedInput
              type="nome"
              name="fathers_name"
              value={formData.fathers_name}
              onChange={handleChange}
            />
          </div>

          <div className='form-group'>
            <label>CPF</label>
            <FormattedInput
              type="cpf"
              name="cpf"
              maxLength={11}
              value={formData.cpf}
              onChange={handleChange}
              required
            />
          </div>

          <div className='form-group'>
            <label>RG</label>
            <FormattedInput
              type="rg"
              name="rg"
              value={formData.rg}
              onChange={handleChange}
            />
          </div>

          <div className='form-group'>
            <label>Data de nascimento</label>
            <input
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
            />
          </div>

          <div className='form-group form-field'>
            <label>Nacionalidade</label>
            <input
              type="text"
              value={nationalityName}
              onChange={(e) => handleCountrySearch(e.target.value)}
              placeholder="Digite a nacionalidade"
              name="nationality_country"
              autoComplete="off"
            />
            {filteredCountries.length > 0 && (
              <ul className="suggestions-list">
                {filteredCountries.map((c) => (
                  <li key={c.id} onClick={() => selectCountry(c)}>
                    {c.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className='form-group form-field'>
            <label>Naturalidade</label>
            <input
              type="text"
              value={naturalnessName}
              onChange={(e) => handleCitySearch(e.target.value)}
              placeholder="Digite a naturalidade"
              name="naturalness_city"
              autoComplete="off"
            />
            {filteredCities.length > 0 && (
              <ul className="suggestions-list-cities">
                {filteredCities.map((c) => (
                  <li key={c.id} onClick={() => selectCity(c)}>
                    {c.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className='form-group'>
            <label>Cor ou Etnia</label>
            <select
              name="color_or_ethnicity"
              value={formData.color_or_ethnicity}
              onChange={handleChange}
            >
              <option value=''>Selecione</option>
              {colorsEthnicity.map(color => (
                <option key={color.id} value={color.id}>{safeText(color.name)}</option>
              ))}
            </select>
          </div>
          <div className='form-group'>
            <label>Gênero</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value=''>Selecione</option>
              {genders.map(gender => (
                <option key={gender.id} value={gender.id}>{safeText(gender.name)}</option>
              ))}
            </select>
          </div>
          <div className='form-group'>
            <label>Orientação sexual</label>
            <select
              name="sexual_orientation"
              value={formData.sexual_orientation}
              onChange={handleChange}
            >
              <option value=''>Selecione</option>
              {sexualOrientations.map(sexual_orientation => (
                <option key={sexual_orientation.id} value={sexual_orientation.id}>{safeText(sexual_orientation.name)}</option>
              ))}
            </select>
          </div>
          <div className='form-group'>
            <label>Estado Civil</label>
            <select
              name="marital_status"
              value={formData.marital_status}
              onChange={handleChange}
            >
              <option value=''>Selecione</option>
              {maritalStatuses.map(status => (
                <option key={status.id} value={status.id}>{safeText(status.name)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className='form-group'>
          <label>
            <input
              type="checkbox"
              checked={formData.has_biometrics}
              onChange={(e) => setFormData(prev => ({ ...prev, has_biometrics: e.target.checked }))}
            />
            Biometria feita?
          </label>
        </div>

        <div className='form-group'>
          <label>Rji biometria:</label>
          <input
            type="text"
            name="rji_biometrics"
            value={formData.rji_biometrics}
            onChange={handleChange}
          />
        </div>
        <div className='form-group'>
          <label>Tguid biometria:</label>
          <input
            type="text"
            name="tguid_biometrics"
            value={formData.tguid_biometrics}
            onChange={handleChange}
          />
        </div>
        <div className='form-group'>
          <label>Observações biometria:</label>
          <input
            type="text"
            name="biometrics_observation"
            value={formData.biometrics_observation}
            onChange={handleChange}
          />
        </div>



        <button type="submit" className="save-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : 'Salvar'}
        </button>
      </form>

      <div className="link-internal-section">
        <h4 className="link-internal-title">Vínculo de Cadastro Duplicado</h4>
        <p className="link-internal-info">
          Utilize esta seção quando este cadastro for um duplicado. Selecione o cadastro <strong>principal</strong> ao qual este deve ser vinculado. Um principal pode ter vários secundários, mas cada secundário aponta para apenas um principal.
        </p>

        {currentLink ? (
          <div className="link-internal-current">
            <span>Vinculado a: <strong>{currentLink.name}</strong></span>
            <button
              type="button"
              className="link-internal-unlink-btn"
              onClick={() => setShowUnlinkConfirm(true)}
              disabled={isLinkSaving}
            >
              Desvincular
            </button>
          </div>
        ) : (
          <div className="link-internal-search">
            <div className="search-container">
              <div className="search-form">
                <div className="search-row">
                  <div className="search-wrapper">
                    <input
                      type="text"
                      value={linkSearch}
                      onChange={(e) => { setLinkSearch(e.target.value); setSelectedLink(null); }}
                      placeholder="Buscar por nome ou CPF"
                      autoComplete="off"
                    />
                    <img src={Lupa} alt="Lupa" className="search-icon" />
                    {(isLinkSearching || linkResults.length > 0) && linkSearch.length >= 3 && (
                      <div className="social-search-results">
                        {isLinkSearching ? (
                          <div className="social-search-result-item search-result-loading">Buscando...</div>
                        ) : (
                          linkResults.map((r) => (
                            <div
                              key={r.id}
                              className="social-search-result-item"
                              onClick={() => handleLinkSelect(r)}
                            >
                              {r.name}{r.cpf ? ` — CPF: ${r.cpf}` : ''}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    {!isLinkSearching && linkSearch.length >= 3 && linkResults.length === 0 && (
                      <div className="social-search-results">
                        <div className="social-search-result-item search-result-empty">Nenhum interno encontrado.</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {selectedLink && (
              <div className="link-internal-selected">
                <span>Selecionado: <strong>{selectedLink.name}</strong></span>
                <button type="button" className="save-btn" onClick={() => setShowLinkConfirm(true)} disabled={isLinkSaving}>
                  Vincular
                </button>
                <button type="button" className="link-internal-unlink-btn" onClick={() => setSelectedLink(null)} disabled={isLinkSaving}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={showLinkConfirm}
        closeModal={() => setShowLinkConfirm(false)}
        title="Confirmar vínculo"
      >
        <div style={{ padding: '16px 0' }}>
          <p>Deseja vincular este cadastro ao interno <strong>{selectedLink?.name}</strong>?</p>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>Este cadastro será marcado como secundário.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" className="link-internal-unlink-btn" onClick={() => setShowLinkConfirm(false)} disabled={isLinkSaving}>
            Cancelar
          </button>
          <button type="button" className="save-btn" onClick={handleSaveLink} disabled={isLinkSaving}>
            {isLinkSaving ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showUnlinkConfirm}
        closeModal={() => setShowUnlinkConfirm(false)}
        title="Confirmar desvinculação"
      >
        <div style={{ padding: '16px 0' }}>
          <p>Deseja desvincular este cadastro de <strong>{currentLink?.name}</strong>?</p>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>O cadastro voltará ao estado independente.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" className="link-internal-unlink-btn" onClick={() => setShowUnlinkConfirm(false)} disabled={isLinkSaving}>
            Cancelar
          </button>
          <button type="button" className="save-btn" onClick={handleUnlink} disabled={isLinkSaving}>
            {isLinkSaving ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default EditInternal;
